import {
  YES_NO,
  applicantSection,
  feeInfo,
  otherAttachments,
  parcelsField,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { ROAD_ORGAN } from "modules/ebiForms/definitions/sharedRoads";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * D3. Wniosek o zezwolenie na lokalizację urządzenia obcego lub reklamy w pasie drogowym albo o uzgodnienie projektu —
 * art. 39 ust. 3–3b ustawy o drogach publicznych (tekst Dz.U. 2025 poz. 889 — pobrany 2026-10-22: tak). Dwie powiązane
 * sprawy jako warianty: (1) decyzja lokalizacyjna z art. 39 ust. 3, (2) uzgodnienie projektu zagospodarowania działki lub
 * terenu i projektu architektoniczno-budowlanego urządzenia (art. 39 ust. 3a pkt 2) — z odwołaniem do decyzji (1).
 * Brak wzoru ogólnopolskiego; układ jak wzór Gminy Lądek
 * (https://www.gminaladek.pl/asp/pliki/wzory_wnioskow/wniosek-o-wydanie-zezwolenia-na-lokalizacje-w-pasie-drogowym-obiektow-budowlanych-urzadzen-lub-reklam-1.pdf
 * — pobrany: tak): rodzaj (obiekt / urządzenie / reklama) z opisem i wymiarami, droga, adres, działki, inne informacje;
 * pola urządzeń liniowych (długość, technologia, warunki gestora) jak wzór GDDKiA U-5
 * (https://www.gov.pl/attachment/ea3b9223-2bf1-45a9-a4f2-c1db7e176949 — pobrany: tak, drogi krajowe jako odniesienie).
 * Oświadczenie o kanale technologicznym dla telekomunikacji — art. 39 ust. 3 pkt 1a. Uzgodnienie trasy sieci przed naradą
 * koordynacyjną (ZDM Warszawa KU7a) to praktyka jednego zarządcy — nie jest osobnym wariantem.
 */
const LOCATION: EbiCondition = { field: "rodzajSprawy", equals: "lokalizacja" };
const PROJECT: EbiCondition = { field: "rodzajSprawy", equals: "uzgodnienie" };
const LINEAR: EbiCondition = { field: "rodzajUrzadzenia", equals: "liniowe" };
const OTHER_DEVICE: EbiCondition = { field: "rodzajUrzadzenia", equals: "inne" };
const ADVERT: EbiCondition = { field: "rodzajUrzadzenia", equals: "reklama" };
const DEVICE: EbiCondition = { field: "rodzajUrzadzenia", in: ["liniowe", "inne"] };
const TELECOM: EbiCondition = { all: [DEVICE, { field: "infrastrukturaTelekom", equals: "tak" }] };

const D3: IEbiFormDefinition = {
  code: "D3",
  version: 1,
  title: "Wniosek o zezwolenie na lokalizację urządzenia obcego lub reklamy w pasie drogowym albo uzgodnienie projektu",
  category: EEbiFormCategory.Roads,
  office: EEbiFormOffice.MunicipalUnit,
  description: "Zezwolenie na lokalizację w pasie drogi gminnej urządzenia obcego (np. sieci) lub reklamy albo uzgodnienie projektu takiego urządzenia.",
  keywords: ["urządzenie obce", "sieć", "uzbrojenie terenu", "lokalizacja w pasie drogowym", "uzgodnienie projektu", "reklama", "droga gminna"],
  organ: ROAD_ORGAN.municipalUnit,
  legalBasis: "art. 39 ust. 3–3b ustawy z dnia 21 marca 1985 r. o drogach publicznych",
  request:
    "Wnoszę o wydanie zezwolenia na lokalizację w pasie drogowym drogi gminnej urządzenia obcego lub reklamy albo o uzgodnienie projektu — w zakresie wskazanym poniżej.",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty skarbowej; pełnomocnictwo — 17 zł. Za późniejsze umieszczenie urządzenia lub reklamy w pasie drogowym zarządca pobiera opłatę za zajęcie pasa (art. 40 ustawy o drogach publicznych).",
  deadlineLabel: "do 65 dni",
  deadline: "Zezwolenie na lokalizację — do 65 dni, dla infrastruktury telekomunikacyjnej do 45 dni od złożenia wniosku (art. 39 ust. 3b ustawy o drogach publicznych).",
  variantField: "rodzajSprawy",
  variants: {
    lokalizacja: { title: "Wniosek o wydanie zezwolenia na lokalizację w pasie drogowym urządzenia obcego lub reklamy" },
    uzgodnienie: { title: "Wniosek o uzgodnienie projektu urządzenia obcego umieszczanego w pasie drogowym" },
  },
  sections: [
    applicantSection(),
    representationSection(),
    {
      code: "sprawa",
      title: "Rodzaj sprawy",
      fields: [
        {
          code: "rodzajSprawy",
          type: "radio",
          label: "Wnoszę o",
          required: true,
          options: [
            { value: "lokalizacja", label: "wydanie zezwolenia na lokalizację w pasie drogowym urządzenia obcego lub reklamy (art. 39 ust. 3)" },
            {
              value: "uzgodnienie",
              label:
                "uzgodnienie projektu zagospodarowania działki lub terenu oraz projektu architektoniczno-budowlanego urządzenia umieszczanego w pasie drogowym (art. 39 ust. 3a pkt 2)",
            },
          ],
        },
        {
          code: "decyzjaLokalizacyjnaZnak",
          type: "text",
          label: "Znak decyzji zezwalającej na lokalizację urządzenia w pasie drogowym",
          visibleWhen: PROJECT,
          width: "twoThirds",
          maxLength: 100,
        },
        { code: "decyzjaLokalizacyjnaData", type: "date", label: "Data decyzji", visibleWhen: PROJECT, width: "third" },
      ],
    },
    {
      code: "przedmiot",
      title: "Urządzenie obce lub reklama",
      fields: [
        {
          code: "rodzajUrzadzenia",
          type: "radio",
          label: "Przedmiot wniosku",
          required: true,
          options: [
            {
              value: "liniowe",
              label:
                "liniowe urządzenie obce — sieć lub przyłącze (wodociągowe, kanalizacyjne, gazowe, ciepłownicze, elektroenergetyczne, telekomunikacyjne)",
            },
            { value: "inne", label: "inne urządzenie lub obiekt budowlany niezwiązany z potrzebami zarządzania drogami lub ruchu drogowego" },
            { value: "reklama", label: "reklama" },
          ],
        },
        {
          code: "opis",
          type: "textarea",
          label: "Rodzaj i opis urządzenia, obiektu lub reklamy oraz ich wymiary",
          required: true,
          maxLength: 3000,
        },
        {
          code: "infrastrukturaTelekom",
          type: "radio",
          label: "Czy urządzenie stanowi infrastrukturę telekomunikacyjną?",
          required: true,
          visibleWhen: DEVICE,
          options: YES_NO,
        },
        {
          code: "kanalTechnologiczny",
          type: "declaration",
          label:
            "Oświadczam, że na odcinku drogi objętym wnioskiem nie ma kanału technologicznego z wolnymi zasobami, w którym można umieścić tę infrastrukturę telekomunikacyjną (art. 39 ust. 3 pkt 1a ustawy o drogach publicznych).",
          required: true,
          visibleWhen: TELECOM,
        },
        {
          code: "dlugoscUrzadzenia",
          type: "number",
          label: "Długość urządzenia w pasie drogowym [m]",
          required: true,
          visibleWhen: LINEAR,
          decimals: 2,
          min: 0.01,
          width: "half",
        },
        {
          code: "powierzchniaRzutu",
          type: "number",
          label: "Powierzchnia rzutu poziomego urządzenia w pasie drogowym [m²]",
          visibleWhen: DEVICE,
          requiredWhen: OTHER_DEVICE,
          decimals: 2,
          min: 0.01,
          width: "half",
        },
        {
          code: "powierzchniaReklamy",
          type: "number",
          label: "Powierzchnia reklamy [m²]",
          required: true,
          visibleWhen: ADVERT,
          decimals: 2,
          min: 0.01,
          width: "half",
        },
        {
          code: "przebiegUrzadzenia",
          type: "checkboxes",
          label: "Przebieg urządzenia",
          visibleWhen: LINEAR,
          options: [
            { value: "wzdluz", label: "wzdłuż drogi" },
            { value: "wPoprzek", label: "w poprzek drogi" },
          ],
        },
        {
          code: "usytuowanieUrzadzenia",
          type: "checkboxes",
          label: "Usytuowanie urządzenia",
          visibleWhen: DEVICE,
          options: [
            { value: "wJezdni", label: "w jezdni" },
            { value: "pozaJezdnia", label: "poza jezdnią" },
            { value: "obiektInzynierski", label: "na drogowym obiekcie inżynierskim (np. moście)" },
          ],
        },
        {
          code: "technologiaRobot",
          type: "text",
          label: "Technologia prowadzenia robót (np. przewiert, przecisk, wykop otwarty)",
          visibleWhen: LINEAR,
          maxLength: 300,
        },
        {
          code: "uzasadnienie",
          type: "textarea",
          label: "Uzasadnienie lokalizacji w pasie drogowym",
          visibleWhen: LOCATION,
          maxLength: 3000,
          hint: "Lokalizacja urządzeń obcych i reklam w pasie drogowym jest dopuszczalna w szczególnie uzasadnionych przypadkach (art. 39 ust. 3) — opisz, dlaczego jest potrzebna.",
        },
      ],
    },
    {
      code: "lokalizacja",
      title: "Miejsce planowanej lokalizacji",
      fields: [
        { code: "drogaNumer", type: "text", label: "Droga gminna — numer drogi albo nazwa ulicy", required: true, width: "half", maxLength: 200 },
        { code: "miejscowoscLokalizacji", type: "text", label: "Miejscowość", required: true, width: "half", maxLength: 100 },
        {
          code: "odcinekDrogi",
          type: "text",
          label: "Odcinek drogi (od km do km albo między punktami charakterystycznymi) i strona drogi",
          maxLength: 300,
        },
        parcelsField({ label: "Działki ewidencyjne planowanej lokalizacji" }),
        { code: "inneInformacje", type: "textarea", label: "Inne informacje", maxLength: 2000 },
      ],
    },
    {
      code: "informacje",
      title: "Po wydaniu zezwolenia",
      fields: [
        {
          ...feeInfo(
            "infoKolejneKroki",
            "W zezwoleniu zarządca drogi określa rodzaj inwestycji, sposób, miejsce i warunki jej umieszczenia w pasie drogowym. Przed rozpoczęciem robót trzeba uzgodnić z zarządcą drogi projekt zagospodarowania działki lub terenu oraz projekt architektoniczno-budowlany urządzenia (przed uzyskaniem pozwolenia na budowę), uzyskać pozwolenie na budowę albo dokonać zgłoszenia, a następnie uzyskać zezwolenie zarządcy drogi na zajęcie pasa drogowego (art. 39 ust. 3a).",
          ),
          visibleWhen: LOCATION,
        },
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "planSytuacyjny",
      label: "Szczegółowy plan sytuacyjny (mapa zasadnicza) w skali 1:1000 lub 1:500 z proponowaną lokalizacją lub przebiegiem urządzenia, obiektu albo reklamy",
      required: true,
    },
    {
      code: "projektDoUzgodnienia",
      label: "Projekt zagospodarowania działki lub terenu oraz projekt architektoniczno-budowlany urządzenia",
      visibleWhen: PROJECT,
      required: true,
    },
    {
      code: "projektTechniczny",
      label: "Projekt techniczny lub przekrój poprzeczny z głębokością posadowienia urządzenia i opisem technologii robót",
      visibleWhen: { all: [LOCATION, LINEAR] },
      required: true,
    },
    {
      code: "rysunekObiektu",
      label: "Rysunek obiektu, urządzenia lub reklamy z wymiarami i szkicem konstrukcyjnym",
      visibleWhen: { all: [LOCATION, { field: "rodzajUrzadzenia", in: ["inne", "reklama"] }] },
      required: true,
    },
    {
      code: "warunkiGestora",
      label: "Warunki techniczne wydane przez gestora sieci",
      hint: "Nieobowiązkowo — niektórzy zarządcy dróg o nie proszą.",
      visibleWhen: LINEAR,
    },
    {
      code: "naradaKoordynacyjna",
      label: "Protokół z narady koordynacyjnej sytuowania projektowanych sieci uzbrojenia terenu",
      hint: "Nieobowiązkowo — niektórzy zarządcy dróg o niego proszą.",
      visibleWhen: LINEAR,
    },
    {
      code: "wypisMpzp",
      label: "Wypis i wyrys z miejscowego planu zagospodarowania przestrzennego albo decyzja o warunkach zabudowy lub o lokalizacji inwestycji celu publicznego",
      hint: "Nieobowiązkowo — jeśli dla terenu obowiązuje plan miejscowy albo wydano taką decyzję.",
    },
    {
      code: "wypisRejestruGruntow",
      label: "Wypis z rejestru gruntów dla działek, na których ma być zlokalizowane urządzenie, obiekt lub reklama",
      hint: "Nieobowiązkowo — niektórzy zarządcy dróg o niego proszą.",
    },
    {
      code: "decyzjaLokalizacyjna",
      label: "Decyzja zezwalająca na lokalizację urządzenia w pasie drogowym (art. 39 ust. 3)",
      hint: "Nieobowiązkowo — wystarczą znak i data decyzji podane we wniosku.",
      visibleWhen: PROJECT,
    },
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default D3;
